import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { EventOccurrenceWithBookings } from '@/types/events';
import { OccurrenceSelector } from '@/components/events/OccurrenceSelector';
import { CheckCircle, Users, Clock, Minus, Plus, Calendar, Info, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { UseMutationResult } from '@tanstack/react-query';

import { getPriceCopy } from '@/lib/events/priceCopy';
import { track } from '@/lib/analytics';
import { spotsBucket, spotsRequestedBucket, classifyFailure } from '@/lib/analytics-helpers';
import { captureAppError } from '@/lib/monitoring/sentry';
import { VisitorShareDialog } from '@/features/share/visitor/VisitorShareDialog';
import { shouldReportToSentry } from '@/lib/monitoring/sentryGate';
import { useTranslate } from '@/i18n';

type BookingMode = 'none' | 'optional' | 'required';

interface EventBookingPanelProps {
  event: {
    id: string;
    producer_id: string;
    title: string;
    is_free: boolean;
    is_recurring: boolean;
    price_ore: number;
    max_spots: number;
    booked_spots: number;
    booking_deadline_hours: number;
    start_date: string;
    end_date: string;
    booking_mode?: string;
    event_type?: string;
  };
  occurrences: EventOccurrenceWithBookings[] | undefined;
  producerName?: string;
  producerSlug?: string;
  venueText?: string | null;
  stats: { seats_left: number; capacity_total: number } | null | undefined;
  refetchStats: () => void;
  createBooking: UseMutationResult<any, Error, any>;
}

function deadlineCopyShort(hours: number): string {
  if (hours >= 24 && hours % 24 === 0) {
    const days = hours / 24;
    return `${days} ${days === 1 ? 'dag' : 'dagar'}`;
  }
  return `${hours} ${hours === 1 ? 'timme' : 'timmar'}`;
}

export function EventBookingPanel({
  event,
  occurrences,
  producerName,
  producerSlug,
  venueText,
  stats,
  refetchStats,
  createBooking,
}: EventBookingPanelProps) {
  const t = useTranslate();
  const [bookingForm, setBookingForm] = useState({ name: '', email: '', spots: 1 });
  const [emailDirty, setEmailDirty] = useState(false);
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [showVisitorShare, setShowVisitorShare] = useState(false);
  const modalOpenTrackedRef = useRef(false);

  const bookingMode: BookingMode = (event.booking_mode as BookingMode) || 'none';
  const isFree = Boolean(event.is_free) || (event.price_ore ?? 0) <= 0;
  const priceCopy = getPriceCopy(event);

  // Course series: event_type='course' with multiple occurrences
  const isCourseSeries = event.event_type === 'course' && event.is_recurring && (occurrences?.length ?? 0) > 1;

  // ── Analytics: booking_modal_opened (once per mount) ──
  useEffect(() => {
    if (modalOpenTrackedRef.current) return;
    modalOpenTrackedRef.current = true;
    track('booking_modal_opened', {
      activity_id: event.id,
      producer_id: event.producer_id,
      booking_mode: bookingMode,
      price_bucket: isFree ? 'free' : 'paid',
      spots_bucket: spotsBucket(event.max_spots),
    });
  }, [event.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Capacity ──
  const hasCapacityData = typeof event.max_spots === 'number' && event.max_spots > 0;
  const isRecurring = event.is_recurring && occurrences && occurrences.length > 0;

  const selectedOccurrence = isRecurring && selectedOccurrenceId
    ? occurrences?.find(o => o.id === selectedOccurrenceId)
    : null;

  const spotsLeft = hasCapacityData
    ? (stats?.seats_left ?? (isRecurring && selectedOccurrence
        ? (selectedOccurrence.capacity ?? event.max_spots) - selectedOccurrence.booked_spots
        : event.max_spots - event.booked_spots))
    : null;

  const isFullyBooked = spotsLeft !== null && spotsLeft <= 0;

  // Max for stepper: min(10, spots_left) if capacity exists, else 10
  const maxSpots = spotsLeft !== null && spotsLeft > 0 ? Math.min(10, spotsLeft) : 10;

  // ── Deadline ──
  const eventStartDate = isRecurring && selectedOccurrence
    ? new Date(selectedOccurrence.start_at)
    : new Date(event.start_date);
  const deadlineDate = new Date(eventStartDate.getTime() - event.booking_deadline_hours * 3600000);
  const isBookingClosed = new Date() > deadlineDate;

  // Prefill email from logged-in user (without overwriting manual input)
  useEffect(() => {
    if (emailDirty) return;
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.email && !emailDirty) {
        setBookingForm(prev => prev.email === '' ? { ...prev, email: data.user!.email! } : prev);
      }
    });
  }, [emailDirty]);

  // Clamp spots when spotsLeft changes
  useEffect(() => {
    if (spotsLeft !== null && bookingForm.spots > Math.min(10, spotsLeft)) {
      setBookingForm(prev => ({ ...prev, spots: Math.max(1, Math.min(10, spotsLeft)) }));
    }
  }, [spotsLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bookingForm.name.trim() || !bookingForm.email.trim()) {
      toast.error('Fyll i namn och e-post');
      return;
    }
    if (isRecurring && !isCourseSeries && !selectedOccurrenceId) {
      toast.error('Välj ett tillfälle att boka');
      return;
    }

    // Guard: fullbooked
    if (isFullyBooked) {
      track('booking_failed', {
        activity_id: event.id,
        producer_id: event.producer_id,
        failure_reason: 'fullbooked',
      });
      toast.error(t('booking.event.fullyBooked'));
      return;
    }

    // Guard: deadline passed
    if (isBookingClosed) {
      track('booking_failed', {
        activity_id: event.id,
        producer_id: event.producer_id,
        failure_reason: 'deadline',
      });
      toast.error(t('booking.event.closed'));
      return;
    }

    if (spotsLeft !== null && bookingForm.spots > spotsLeft) {
      toast.error(`Det finns bara ${spotsLeft} platser kvar`);
      return;
    }

    const quantityToSend = bookingForm.spots;

    // ── Analytics: booking_submitted ──
    track('booking_submitted', {
      activity_id: event.id,
      producer_id: event.producer_id,
      spots_requested_bucket: spotsRequestedBucket(quantityToSend),
      booking_mode: bookingMode,
    });

    try {
      const booking = await createBooking.mutateAsync({
        event_id: event.id,
        producer_id: event.producer_id,
        occurrence_id: (isRecurring && !isCourseSeries) ? selectedOccurrenceId : null,
        guest_name: bookingForm.name,
        guest_email: bookingForm.email,
        spots: quantityToSend,
      });

      const bookingDate = isRecurring && selectedOccurrence
        ? selectedOccurrence.start_at
        : event.start_date;

      // Confirmation email is now queued server-side inside create-event-booking.
      // No separate frontend call needed.

      refetchStats();
      setBookingSuccess(true);
      track('booking_succeeded', { activity_id: event.id, producer_id: event.producer_id });
      track('booking_completed', {
        booking_id: booking.id,
        activity_id: event.id,
        producer_id: event.producer_id ?? null,
        place_id: null,
        spots: quantityToSend,
        price_ore: isFree ? 0 : event.price_ore ?? null,
      });
      toast.success('Din bokning är bekräftad!');
    } catch (error) {
      console.error('Booking failed:', error);
      const bookingReason = classifyFailure(error);
      track('booking_failed', {
        activity_id: event.id,
        producer_id: event.producer_id,
        failure_reason: bookingReason,
      });
      if (shouldReportToSentry('booking', bookingReason)) {
        captureAppError(error, {
          feature: 'booking',
          action: 'submit',
          event_id: event.id,
          producer_id: event.producer_id,
          failure_reason: bookingReason,
        });
      }
      refetchStats();
      // Map known backend error codes to friendly Swedish messages
      const rawMsg = error instanceof Error ? error.message : '';
      const friendlyBookingError =
        rawMsg.includes('fullbokat') || rawMsg.includes('FULLY_BOOKED') ? 'Aktiviteten är fullbokad.' :
        rawMsg.includes('platser kvar') || rawMsg.includes('NOT_ENOUGH_SPOTS') ? rawMsg :
        rawMsg.includes('DEADLINE_PASSED') || rawMsg.includes('deadline') ? 'Anmälningstiden har gått ut.' :
        rawMsg.includes('EVENT_NOT_FOUND') ? 'Aktiviteten hittades inte.' :
        'Bokningen kunde inte genomföras. Försök igen.';
      toast.error(friendlyBookingError);
    }
  };

  // ── Stepper helpers ──
  const decrement = () => setBookingForm(prev => ({ ...prev, spots: Math.max(1, prev.spots - 1) }));
  const increment = () => setBookingForm(prev => ({ ...prev, spots: Math.min(maxSpots, prev.spots + 1) }));

  // ── Submit button state ──
  const isSubmitDisabled =
    createBooking.isPending ||
    (isRecurring && !isCourseSeries && !selectedOccurrenceId) ||
    isFullyBooked ||
    isBookingClosed;

  const submitLabel = isFullyBooked
    ? t('status.fullyBooked')
    : isBookingClosed
      ? t('booking.event.closed')
      : createBooking.isPending
        ? (bookingMode === 'required' ? 'Bokar...' : 'Skickar...')
        : (bookingMode === 'required' ? 'Boka' : 'Skicka intresse');

  return (
    <div className="space-y-4">
      {/* Deadline info */}
      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
        {hasCapacityData && spotsLeft !== null && (
          <span className="flex items-center gap-1.5">
            <Users className="h-4 w-4" />
            {isCourseSeries
              ? (isFullyBooked
                  ? 'Inga platser kvar'
                  : `${Math.max(spotsLeft ?? 0, 0)} platser kvar`)
              : isRecurring && !selectedOccurrenceId
                ? `${event.max_spots} platser per tillfälle`
                : isFullyBooked
                  ? 'Inga platser kvar'
                  : `${Math.max(spotsLeft ?? 0, 0)} platser kvar`}
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <Clock className="h-4 w-4" />
          Anmälan stänger {deadlineCopyShort(event.booking_deadline_hours)} före start
        </span>
      </div>

      {bookingSuccess ? (
        <div className="py-6 space-y-5" data-testid="booking-success">
          <div className="text-center">
            <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">{t("booking.event.confirmedTitle")}</h3>
            <p className="text-muted-foreground text-sm">
              {t("booking.event.confirmedBody")}
            </p>
          </div>

          {/* Visitor share prompt */}
          {!showVisitorShare ? (
            <button
              onClick={() => setShowVisitorShare(true)}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-border bg-muted/10 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors"
            >
              <Share2 className="h-4 w-4" />
              Tipsa en vän om aktiviteten
            </button>
          ) : (
            <VisitorShareDialog
              target={{
                type: 'activity',
                id: event.id,
                title: event.title,
                heroImageUrl: null,
                producerName: producerName || '',
                producerSlug: producerSlug || '',
                startDate: event.start_date,
                endDate: event.end_date,
                isFree: event.is_free,
                priceOre: event.price_ore,
                bookingMode: event.booking_mode || 'none',
                venueText: venueText,
              }}
              onClose={() => setShowVisitorShare(false)}
            />
          )}
        </div>
      ) : !isRecurring && isFullyBooked ? (
        <div className="text-center py-6">
          <p className="text-muted-foreground">{t("booking.event.fullyBooked")}</p>
        </div>
      ) : !isRecurring && isBookingClosed ? (
        <div className="text-center py-6">
          <p className="text-muted-foreground">{t("booking.event.closed")}</p>
        </div>
      ) : (
        <form onSubmit={handleBookingSubmit} className="space-y-4">
          {/* Course series: show date list (read-only, no selection) */}
          {isCourseSeries && occurrences && (
            <div className="pb-4 border-b space-y-3">
              <p className="text-sm font-medium">Kursens tillfällen</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {occurrences
                  .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())
                  .map((occ) => (
                    <div key={occ.id} className="flex items-center gap-2.5 py-1.5 px-3 rounded-md bg-muted/40 text-sm">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="font-medium">
                        {format(new Date(occ.start_at), "EEEE d MMMM", { locale: sv })}
                      </span>
                      <span className="text-muted-foreground">
                        {format(new Date(occ.start_at), "HH:mm")}–{format(new Date(occ.end_at), "HH:mm")}
                      </span>
                    </div>
                  ))}
              </div>
              <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded-md p-2.5">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>Bokningen gäller alla tillfällen i kursen.</span>
              </div>
            </div>
          )}

          {/* Regular recurring: occurrence selector */}
          {isRecurring && !isCourseSeries && occurrences && (
            <div className="pb-4 border-b">
              <OccurrenceSelector
                occurrences={occurrences}
                selectedOccurrenceId={selectedOccurrenceId}
                onSelect={setSelectedOccurrenceId}
                maxSpots={event.max_spots}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="booking-name">Namn *</Label>
            <Input
              id="booking-name"
              data-testid="booking-name"
              value={bookingForm.name}
              onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
              placeholder="Ditt namn"
              required
              autoComplete="name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="booking-email">E-post *</Label>
            <Input
              id="booking-email"
              data-testid="booking-email"
              type="email"
              value={bookingForm.email}
              onChange={(e) => {
                setEmailDirty(true);
                setBookingForm({ ...bookingForm, email: e.target.value });
              }}
              placeholder="din@email.se"
              required
              autoComplete="email"
            />
          </div>

          {/* ── Antal personer (stepper) ── */}
          <div className="space-y-2">
            <Label>Antal personer</Label>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={decrement}
                disabled={bookingForm.spots <= 1}
                aria-label="Minska antal"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-8 text-center font-medium tabular-nums">
                {bookingForm.spots}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={increment}
                disabled={bookingForm.spots >= maxSpots}
                aria-label="Öka antal"
              >
                <Plus className="h-4 w-4" />
              </Button>
              {spotsLeft !== null && spotsLeft > 0 && (
                <span className="text-xs text-muted-foreground ml-1">
                  {spotsLeft} {spotsLeft === 1 ? 'plats' : 'platser'} kvar
                </span>
              )}
            </div>
          </div>

          {/* ── Price + CTA block ── */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">{priceCopy.primary}</span>
              {!isFree && bookingForm.spots > 1 && (
                <span className="text-sm font-medium">
                  {((event.price_ore / 100) * bookingForm.spots).toLocaleString('sv-SE')} kr totalt
                </span>
              )}
            </div>
            {priceCopy.secondary && (
              <p className="text-xs text-muted-foreground">{priceCopy.secondary}</p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitDisabled}
              data-testid="booking-submit"
            >
              {submitLabel}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
